<template lang="pug">
EcreativMoleculesLocationBaseCard.meetFormCard(variant="base-card--form")
  template(#header)
    EcreativMoleculesLocationCardHeader(hasBorder)
      template(#title)
        EcreativArchetypesHeading.mb-0.px-4.pt-3.pb-2(tag="h3" size="4") {{ heading }}
  EcreativArchePrimitivesParagraph.meetForm__description.px-4.py-3.mb-0(size="7" context="primary") {{ description }}
  DynamicForm.px-4.pb-4(
    :send-email="false"
    :save-firebase="false"
    :schema="formSchema"
    :isFeedBackTooltip="false"
    form-ref="MeetForm"
    :big-form="false"
    :isLoading="loading"
    context="info"
    @formBuilderOnSubmit="onFormSubmit"
  )
    template(#loader)
      EcreativAtomsSpinner
    template(#submitText)
      span.text-white.text-uppercase.fw-bold Find Nearest Imam
    template(#privacyPolicy)
      p.m-0.mb-2
        b
          | I have read and agree to the&nbsp;
          nuxt-link(target="_blank" to="https://docs.trueislam.co.uk/ama-uk-privacy-notice.pdf") Privacy Policy
</template>

<script setup lang="ts">
import * as Yup from 'yup';
import { storeToRefs } from 'pinia';
import { useContactStore } from '@/stores/meetContact';

defineProps({
  heading: { type: String, default: 'Reach Out to an Imam' },
  description: {
    type: String,
    default:
      'Please provide your details to search for your local Imam. You’ll be able to choose how to contact them in the next step.',
  },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits<{
  (
    e: 'find',
    payload: { title: string; name: string; email: string; postcode: string },
  ): void;
}>();

const contactStore = useContactStore();
const { title, name, email, postcode } = storeToRefs(contactStore);

// Client-provided UK postcode regex
const postcodeRegex =
  /^((GIR 0AA)|(SAN TA1)|(BFPO ?[0-9]{1,4})|((([A-Z]{1,2}[0-9][0-9A-Z]?) ?[0-9][A-Z]{2})))$/i;

// Use a regular object for schema, only using store values for initial setup
const formSchema = [
  {
    title: '',
    description: '',
    fields: [
      {
        labelText: '',
        name: 'inputTitle',
        as: 'select',
        half: true,
        placeholder: 'Title',
        value: title.value, // initial prefill from store
        children: [
          { value: 'Dr', name: 'inputTitle', labelText: 'Dr' },
          { value: 'Mr', name: 'inputTitle', labelText: 'Mr' },
          { value: 'Mrs', name: 'inputTitle', labelText: 'Mrs' },
          { value: 'Miss', name: 'inputTitle', labelText: 'Miss' },
          { value: 'Ms', name: 'inputTitle', labelText: 'Ms' },
        ],
        rules: Yup.string().required('Please select your title.'),
      },
      {
        labelText: '',
        placeholder: 'Name',
        name: 'name',
        as: 'input',
        type: 'text',
        value: name.value, // initial prefill from store
        rules: Yup.string().required('Please enter your name.'),
      },
      {
        labelText: '',
        placeholder: 'Email Address',
        name: 'email',
        as: 'input',
        type: 'email',
        value: email.value, // initial prefill from store
        rules: Yup.string()
          .email('Please enter a valid email.')
          .matches(
            /@(?:[^\s@.]+\.)+[^\s@.]{2,}$/,
            'Please enter a valid email.',
          )
          .required('Email is required.'),
      },
      {
        labelText: '',
        placeholder: 'Postcode',
        name: 'postcode',
        as: 'input',
        type: 'text',
        value: postcode.value, // initial prefill from store
        // NEW: live formatting in the component
        formatUkPostcode: true,
        rules: Yup.string()
          .transform((v) => {
            if (v == null) return v as unknown as string;
            const raw = String(v).toUpperCase().replace(/\s+/g, '');
            if (raw === '') return '';
            if (raw === 'GIR0AA') return 'GIR 0AA';
            if (raw === 'SANTA1') return 'SAN TA1';
            const bfpo = raw.match(/^BFPO(\d{1,4})$/);
            if (bfpo) return `BFPO ${bfpo[1]}`;
            if (raw.length <= 3) return raw;
            return `${raw.slice(0, raw.length - 3)} ${raw.slice(raw.length - 3)}`;
          })
          .required('Postcode is required')
          .matches(postcodeRegex, 'Please enter a valid UK postcode'),
      },
    ],
  },
];

function onFormSubmit(payload: Record<string, unknown>) {
  // Persist normalized values
  contactStore.SET_CONTACT({
    title: String(payload.inputTitle ?? '').trim(),
    name: String(payload.name ?? '').trim(),
    email: String(payload.email ?? '').trim(),
    postcode: String(payload.postcode ?? '')
      .toUpperCase()
      .replace(/\s+/g, '')
      .replace(/^GIR0AA$/, 'GIR 0AA')
      .replace(/^SANTA1$/, 'SAN TA1')
      .replace(/^BFPO(\d{1,4})$/, (_, d) => `BFPO ${d}`)
      .replace(/^(.*)(.{3})$/, '$1 $2') // ensure space before last 3
      .trim(),
  });

  emit('find', { ...contactStore.getContact });
}

// const form = reactive({
//   title: 'Mr',
//   name: 'Temporary Name',
//   email: 'Temporary Email',
//   postcode: 'SE6 1TP',
// });
</script>

<style scoped lang="scss">
.meetForm__input,
.meetForm__select {
  padding: 16px;
  border: 2px solid $green-200;
  border-radius: $border-radius-lg;
}
</style>
